import {
  flattenSessionEngagementPages,
  useQueryRecentSessionEngagement,
} from '@heidi/session/data';
import { useMemo } from 'react';

import {
  QUALIFIED_SESSION_MIN_DURATION_SECONDS,
  QUALIFIED_SESSION_WINDOW_MS,
  REQUIRED_QUALIFIED_SESSIONS,
} from '../../contracts';

type SessionEngagementCandidate = {
  created_at?: string | null;
  created_time_local?: string | null;
  duration?: number | null;
  session_duration?: number | null;
};

const getSessionDurationSeconds = (session: SessionEngagementCandidate): number =>
  session.duration ?? session.session_duration ?? 0;

const getSessionCreatedAtMs = (session: SessionEngagementCandidate): number => {
  const timestamp = session.created_at ?? session.created_time_local;
  if (!timestamp) return 0;
  const parsed = Date.parse(timestamp);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Shared counter for unit tests and both session-list backends. */
export function countQualifiedSessionEngagement(
  sessions: SessionEngagementCandidate[] | null | undefined,
  nowMs = Date.now()
): boolean {
  if (sessions == null) return false;

  const cutoffMs = nowMs - QUALIFIED_SESSION_WINDOW_MS;
  let qualifiedCount = 0;

  for (const session of sessions) {
    if (getSessionCreatedAtMs(session) < cutoffMs) continue;
    if (getSessionDurationSeconds(session) <= QUALIFIED_SESSION_MIN_DURATION_SECONDS) {
      continue;
    }
    qualifiedCount += 1;
    if (qualifiedCount >= REQUIRED_QUALIFIED_SESSIONS) return true;
  }

  return false;
}

/**
 * True once the user has completed {@link REQUIRED_QUALIFIED_SESSIONS} scribe
 * sessions longer than {@link QUALIFIED_SESSION_MIN_DURATION_SECONDS} within
 * the rolling two-week window. Used only to gate auto-opening the
 * notifications popover on login — pinned cards themselves do not wait on this.
 *
 * Counts from the v2.2 session list, which serves every user regardless of
 * which list UI their `new session list` flag resolves to. Branching on that
 * flag instead would leave one cohort with no session source at all.
 *
 * `enabled` is the caller's gate: the query only runs once there is a pinned
 * recommendation whose auto-open this could unlock.
 *
 * The door hook builds the request, so no params cross: it subscribes to the
 * unfiltered `scope: me` entry the real session list already populates and
 * writes no desktop filter bucket of its own. The two-week cutoff stays
 * client-side, in {@link countQualifiedSessionEngagement}.
 */
export function useHasQualifiedSessionEngagement(enabled = true): boolean {
  const { data, isLoading } = useQueryRecentSessionEngagement(enabled);

  // Flatten inside the memo: hoisting it produced a fresh array identity every
  // render, so the memo recomputed unconditionally.
  return useMemo(() => {
    if (!enabled || isLoading) return false;
    return countQualifiedSessionEngagement(flattenSessionEngagementPages(data?.pages));
  }, [data?.pages, enabled, isLoading]);
}
